import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, firestore } from '../firebase/config';
import { doc, getDoc, collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { useToast } from '../components/ToastContext';
import LoadingSpinner from '../components/LoadingSpinner';
import ChatComponent from '../components/ChatComponent';
import Notes from '../components/Notes';
import StudentQuiz from '../components/StudentQuiz';
import CodeEditor from "../components/CodeEditor";
import { getPakistanTime, getPakistanDate } from '../utils/timeUtils';

function QuizPage() {
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [courseName, setCourseName] = useState('');
  const [quizName, setQuizName] = useState('');
  const [actualQuizName, setActualQuizName] = useState('');
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [user] = useAuthState(auth);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  
  // Quiz overview states
  const [allQuizzes, setAllQuizzes] = useState([]);
  const [quizResults, setQuizResults] = useState([]);
  const [quizOverviewLoading, setQuizOverviewLoading] = useState(false);
  const [showQuizOverview, setShowQuizOverview] = useState(true);

  // Function to fetch quiz overview data
  const fetchQuizOverview = async () => {
    if (!student?.course && !courseName) return;
    
    setQuizOverviewLoading(true);
    try {
      const courseToUse = courseName || student.course || student.courseName;
      const studentUid = user?.uid || (localStorage.getItem('customUser') ? JSON.parse(localStorage.getItem('customUser')).uid : null);
      
      console.log('Fetching quiz overview for:', { courseToUse, studentUid });
      
      // Get all quizzes for this course
      const quizzesQuery = query(
        collection(firestore, 'quizzes'),
        where('courseName', '==', courseToUse)
      );
      const quizzesSnapshot = await getDocs(quizzesQuery);
      const allQuestions = quizzesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Group questions by quiz name
      const quizzesByName = {};
      allQuestions.forEach(question => {
        const name = question.quizName || `${courseToUse} Quiz`;
        if (!quizzesByName[name]) {
          quizzesByName[name] = [];
        }
        quizzesByName[name].push(question);
      });
      
      const quizList = Object.keys(quizzesByName).map(name => ({
        name,
        questionCount: quizzesByName[name].length,
        questions: quizzesByName[name]
      }));
      
      setAllQuizzes(quizList);
      
      // Get student's quiz results
      const resultsQuery = query(
        collection(firestore, 'quizResults'),
        where('studentId', '==', studentUid),
        where('courseName', '==', courseToUse)
      );
      const resultsSnapshot = await getDocs(resultsQuery);
      const results = resultsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Sort by submission date (newest first)
      results.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
      
      setQuizResults(results);
      
      console.log('Quiz overview loaded:', {
        totalQuizzes: quizList.length,
        attemptedQuizzes: results.length,
        quizList,
        results
      });
      
    } catch (error) {
      console.error('Error fetching quiz overview:', error);
      showError('Failed to load quiz overview');
    }
    setQuizOverviewLoading(false);
  };

  // Function to refresh quiz statistics (will be called from StudentQuiz)
  const refreshQuizStats = () => {
    // Refresh the quiz overview data
    fetchQuizOverview();
    // Navigate back to dashboard after a delay
    setTimeout(() => {
      navigate('/student-dashboard');
    }, 2000);
  };

  useEffect(() => {
    const fetchStudent = async () => {
      try {
        // Check for custom authentication first
        const customUser = localStorage.getItem('customUser');
        const customRole = localStorage.getItem('userRole');
        
        let userUid = null;
        
        if (customUser && customRole) {
          const parsedUser = JSON.parse(customUser);
          userUid = parsedUser.uid;
        } else {
          // Fallback to Firebase Auth
          const user = auth.currentUser;
          if (!user) {
            navigate('/login');
            return;
          }
          userUid = user.uid;
        }

        // Fetch student data
        const studentDoc = await getDoc(doc(firestore, 'students', userUid));
        if (studentDoc.exists()) {
          const studentData = studentDoc.data();
          setStudent(studentData);
          const courseToUse = studentData.course || studentData.courseName || '';
          setCourseName(courseToUse);
          
          // Fetch quiz name from the questions
          if (courseToUse) {
            const quizQuery = query(collection(firestore, 'quizzes'), where('courseName', '==', courseToUse));
            const quizSnapshot = await getDocs(quizQuery);
            if (!quizSnapshot.empty) {
              const firstQuestion = quizSnapshot.docs[0].data();
              setActualQuizName(firstQuestion.quizName || courseToUse + ' Quiz');
            } else {
              setActualQuizName(courseToUse + ' Quiz');
            }
            
            // Fetch quiz overview data
            setTimeout(() => {
              fetchQuizOverview();
            }, 500);
          }
        } else {
          showError('Student data not found');
          navigate('/login');
        }
      } catch (error) {
        console.error('Error fetching student:', error);
        showError('Failed to load student data');
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    fetchStudent();
  }, [navigate, showError]);

  const handleLogout = () => {
    localStorage.removeItem('customUser');
    localStorage.removeItem('userRole');
    signOut(auth);
    navigate('/login');
  };

  const handleBackToDashboard = () => {
    navigate('/student-dashboard');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!student) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Student Not Found</h2>
          <button
            onClick={() => navigate('/login')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-blue-50 to-orange-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-orange-600 to-blue-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-3 md:px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 md:h-16">
            {/* Left side - Logo and Navigation */}
            <div className="flex items-center">
              {/* <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-1 md:p-2 rounded-md text-white hover:bg-red hover:bg-opacity-10 transition-colors lg:hidden"
              >
                <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button> */}
              
              <div className="flex items-center ml-2 md:ml-4 lg:ml-0">
                <div className="w-6 h-6 md:w-8 md:h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mr-2 md:mr-3">
                  <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div>
                  <h1 className="text-sm md:text-lg font-bold">Digital Skills House</h1>
                  <p className="text-xs text-blue-100">Student Portal</p>
                </div>
              </div>
            </div>

                         {/* Center - Quiz Title */}
             <div className="hidden md:block flex-1 text-center">
               <h2 className="text-lg md:text-xl font-semibold">{actualQuizName}</h2>
               <p className="text-xs md:text-sm text-blue-100">Test your knowledge</p>
             </div>

            {/* Right side - User info and actions */}
            <div className="flex items-center space-x-2 md:space-x-4">
              {/* Back to Dashboard Button */}
              <button
                onClick={handleBackToDashboard}
                className="hidden lg:flex items-center px-3 py-2 text-sm font-medium text-white bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Dashboard
              </button>

              {/* Mobile Back Button */}
              <button
                onClick={handleBackToDashboard}
                className="lg:hidden p-1 md:p-2 text-white hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors"
                title="Back to Dashboard"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>

              {/* Chat Icon */}
              <button
                onClick={() => setShowChat(!showChat)}
                className="relative p-1 md:p-2 text-white hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </button>

              {/* Notes Icon */}
              <button
                onClick={() => setShowNotes(!showNotes)}
                className="relative p-1 md:p-2 text-white hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>

              {/* User Menu */}
              <div className="relative">
                <div className="flex items-center space-x-2 md:space-x-3">
                  <div className="text-right hidden lg:block">
                    <div className="text-sm font-medium">{student.fullName}</div>
                    <div className="text-xs text-blue-100">{student.course || student.courseName}</div>
                  </div>
                  <div className="w-6 h-6 md:w-8 md:h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <span className="text-xs md:text-sm font-bold">
                      {student.fullName ? student.fullName.charAt(0).toUpperCase() : 'S'}
                    </span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="p-1 md:p-2 text-white hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors"
                    title="Logout"
                  >
                    <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Back Button */}
      <div className="md:hidden bg-white border-b border-gray-200 px-4 py-2">
        <button
          onClick={handleBackToDashboard}
          className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Dashboard
        </button>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quiz Overview Section */}
        {showQuizOverview && (
          <div className="mb-8 flex justify-center px-2 md:px-0">
            <div className="w-full max-w-6xl bg-white rounded-lg shadow-lg p-3 md:p-6" style={{ width: '95%', maxWidth: '70%' }}>
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 md:mb-6 space-y-3 md:space-y-0">
                <div className="text-center md:text-left">
                  <h2 className="text-lg md:text-2xl font-bold text-gray-800">Course Quizzes</h2>
                  <p className="text-sm md:text-base text-gray-600">{courseName || student?.course}</p>
                </div>
                <div className="flex items-center justify-center md:justify-end space-x-3 md:space-x-4">
                  <div className="text-center">
                    <div className="text-xl md:text-2xl font-bold text-blue-600">
                      {quizResults.length}/{allQuizzes.length}
                    </div>
                    <div className="text-xs md:text-sm text-gray-600">Attempted</div>
                  </div>
                  {/* <div className="text-center">
                    <div className="text-xl md:text-2xl font-bold text-green-600">
                      {allQuizzes.length > 0 ? Math.round((quizResults.filter(result => result.isPassed).length / allQuizzes.length) * 100) : 0}%
                    </div>
                    <div className="text-xs md:text-sm text-gray-600">Passed</div>
                  </div> */}
                  <button
                    onClick={fetchQuizOverview}
                    className="bg-blue-100 text-blue-700 px-2 md:px-3 py-1 md:py-2 rounded-lg hover:bg-blue-200 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                </div>
              </div>

              {quizOverviewLoading ? (
                <div className="flex items-center justify-center py-8">
                  <svg className="w-8 h-8 animate-spin text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Progress Bar */}
                  {/* <div className="bg-gray-100 rounded-lg p-3 md:p-4">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-2 space-y-1 sm:space-y-0">
                      <span className="text-xs md:text-sm font-medium text-gray-700 text-center sm:text-left">Overall Progress</span>
                      <span className="text-sm md:text-base font-bold text-green-600 text-center sm:text-right">
                        {allQuizzes.length > 0 ? Math.round((quizResults.filter(result => result.isPassed).length / allQuizzes.length) * 100) : 0}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 md:h-3">
                      <div 
                        className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 md:h-3 rounded-full transition-all duration-300"
                        style={{ 
                          width: `${allQuizzes.length > 0 ? (quizResults.filter(result => result.isPassed).length / allQuizzes.length) * 100 : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div> */}

                  {/* Quiz Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Quiz Name</th>
                          <th className="text-center py-3 px-4 font-semibold text-gray-700">Questions</th>
                          <th className="text-center py-3 px-4 font-semibold text-gray-700">Status</th>
                          <th className="text-center py-3 px-4 font-semibold text-gray-700">Score</th>
                          <th className="text-center py-3 px-4 font-semibold text-gray-700">Correct</th>
                          <th className="text-center py-3 px-4 font-semibold text-gray-700">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allQuizzes.map((quiz, index) => {
                          const quizResult = quizResults.find(result => result.quizName === quiz.name);
                          const isAttempted = !!quizResult;
                          const isPassed = quizResult?.isPassed;
                          
                          return (
                            <tr key={index} className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                              isAttempted 
                                ? isPassed 
                                  ? 'bg-green-25' 
                                  : 'bg-red-25'
                                : 'bg-white'
                            }`}>
                              <td className="py-4 px-4">
                                <div className="font-semibold text-gray-800">{quiz.name}</div>
                              </td>
                              <td className="py-4 px-4 text-center">
                                <span className="font-medium text-gray-700">{quiz.questionCount}</span>
                              </td>
                              <td className="py-4 px-4 text-center">
                                {isAttempted ? (
                                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                                    isPassed 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {isPassed ? 'PASSED' : 'FAILED'}
                                  </span>
                                ) : (
                                  <span className="text-gray-500 bg-gray-100 px-3 py-1 rounded-full text-sm">
                                    Not Attempted
                                  </span>
                                )}
                              </td>
                              <td className="py-4 px-4 text-center">
                                {isAttempted ? (
                                  <span className="font-semibold text-gray-800">{quizResult.score}%</span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="py-4 px-4 text-center">
                                {isAttempted ? (
                                  <span className="font-medium text-gray-700">
                                    {quizResult.correctAnswers}/{quizResult.totalQuestions}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="py-4 px-4 text-center">
                                {isAttempted ? (
                                  <span className="text-gray-600">
                                    {new Date(quizResult.submittedAt).toLocaleDateString()}
                                  </span>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  {allQuizzes.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p>No quizzes available for this course.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

                 {/* Quiz Title for Mobile */}
         <div className="md:hidden text-center mb-6">
           <h2 className="text-2xl font-bold text-gray-800">{actualQuizName}</h2>
           <p className="text-gray-600">Test your knowledge</p>
         </div>

        {/* Toggle Quiz Overview Button */}
        <div className="text-center mb-6">
          <button
            onClick={() => setShowQuizOverview(!showQuizOverview)}
            className="bg-purple-100 text-purple-700 px-4 py-2 rounded-lg hover:bg-purple-200 transition-colors"
          >
            {showQuizOverview ? 'Hide Quiz Overview' : 'Show Quiz Overview'}
          </button>
        </div>

        {/* Quiz Content - 70% width and centered */}
        <div className="flex justify-center">
          <div className="w-full max-w-4xl" style={{ width: '70%' }}>
            <StudentQuiz 
              courseName={courseName} 
              studentId={student?.id} 
              studentUid={user?.uid || (localStorage.getItem('customUser') ? JSON.parse(localStorage.getItem('customUser')).uid : null)} 
              onQuizCompleted={refreshQuizStats}
            />
          </div>
        </div>
      </main>

      {/* Chat Component */}
      {showChat && (
        <ChatComponent
          isOpen={showChat}
          onClose={() => setShowChat(false)}
          userRole="student"
          userId={student.id}
          userName={student.fullName}
        />
      )}

      {/* Notes Component */}
      {showNotes && (
        <Notes
          isOpen={showNotes}
          onClose={() => setShowNotes(false)}
          userRole="student"
          userId={student.id}
          userName={student.fullName}
        />
      )}

      <CodeEditor />
    </div>
  );
}

export default QuizPage;
